import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { PageHeader } from '@/components/AIBadge';
import api, { apiErrorMessage } from '@/lib/api';
import AuthImage from '@/components/AuthImage';
import { toast } from 'sonner';
import {
    AlertTriangle, Shield, Sparkles, ExternalLink, RefreshCw,
    Camera, ChevronRight, Loader2, TrendingUp, Trash2,
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid,
    LineChart, Line,
} from 'recharts';

const ACCENT = '#7C6BEF';
const FLAG_META = {
    critical: { bg: 'rgba(239,68,68,0.15)', color: '#EF4444', label: 'CRÍTICO' },
    warning: { bg: 'rgba(245,158,11,0.15)', color: '#F59E0B', label: 'REVISAR' },
    ok: { bg: 'rgba(16,185,129,0.15)', color: '#10B981', label: 'OK' },
};

function timeAgo(iso) {
    if (!iso) return '';
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return 'ahora';
    if (m < 60) return `hace ${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `hace ${h}h`;
    const d = Math.floor(h / 24);
    return `hace ${d}d`;
}

export default function AdminCriticalFindings() {
    const [data, setData] = useState({ items: [], stats: { critical: 0, warning: 0, total: 0 } });
    const [trends, setTrends] = useState(null);
    const [tab, setTab] = useState('list');
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all | critical | warning
    const [q, setQ] = useState('');
    const [preview, setPreview] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const load = () => {
        setLoading(true);
        const params = {};
        if (filter !== 'all') params.flag = filter;
        Promise.all([
            api.get('/admin/critical-findings', { params }),
            api.get('/admin/critical-findings/trends'),
        ])
            .then(([r, t]) => { setData(r.data); setTrends(t.data); })
            .catch((e) => toast.error(apiErrorMessage(e, 'Error al cargar hallazgos')))
            .finally(() => setLoading(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [filter]);

    // Iter 111 · elimina SOLO el hallazgo IA (no toca foto ni respuesta del auditor).
    const deleteFinding = async (item) => {
        if (!item?.id) { toast.error('No se pudo identificar el hallazgo.'); return; }
        if (!window.confirm(`¿Eliminar este hallazgo?\n\n"${(item.question || '').slice(0, 120)}"\n\nSolo se quita de este panel. La foto y la respuesta del auditor en la auditoría NO se modifican. Esta acción no se puede deshacer.`)) return;
        setDeletingId(item.id);
        try {
            await api.delete(`/admin/critical-findings/${item.id}`);
            toast.success('Hallazgo eliminado.');
            setData((d) => {
                const items = d.items.filter((x) => x.id !== item.id);
                const critical = items.filter((x) => x.ai_flag === 'critical').length;
                const warning = items.filter((x) => x.ai_flag === 'warning').length;
                return { items, stats: { critical, warning, total: items.length } };
            });
            if (preview?.id === item.id) setPreview(null);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'No se pudo eliminar el hallazgo'));
        } finally { setDeletingId(null); }
    };

    const filtered = useMemo(() => {
        const query = q.trim().toLowerCase();
        if (!query) return data.items;
        return data.items.filter((it) =>
            [
                it.question, it.ai_analysis, it.standard_reference,
                it._client?.company, it._client?.name, it._inspection?.industry,
            ].filter(Boolean).join(' ').toLowerCase().includes(query)
        );
    }, [q, data.items]);

    return (
        <AdminLayout title="Hallazgos Críticos">
            <PageHeader
                title="Hallazgos Críticos"
                subtitle="◆ Evidencias marcadas por la IA · El ojo humano puede fallar, la IA no descansa"
                accent="#EF4444"
            >
                <button
                    onClick={load}
                    data-testid="findings-refresh"
                    className="inline-flex items-center gap-2 border-2 px-4 py-3 font-bold hover:bg-black/5 dark:hover:bg-white/5"
                    style={{ borderColor: 'var(--border)' }}
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
                </button>
            </PageHeader>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <StatCard label="Críticos" value={data.stats.critical}
                    color="#EF4444" bg="rgba(239,68,68,0.10)" Icon={AlertTriangle} />
                <StatCard label="Para revisar" value={data.stats.warning}
                    color="#F59E0B" bg="rgba(245,158,11,0.10)" Icon={Sparkles} />
                <StatCard label="Total hallazgos" value={data.stats.total}
                    color={ACCENT} bg="rgba(124,107,239,0.10)" Icon={Shield} />
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 border-b" style={{ borderColor: 'var(--border)' }}>
                <button onClick={() => setTab('list')} data-testid="tab-list"
                    className="px-4 py-2 text-sm font-bold border-b-2 inline-flex items-center gap-2"
                    style={{ borderColor: tab === 'list' ? ACCENT : 'transparent', color: tab === 'list' ? ACCENT : '#6B7280' }}>
                    <Camera className="w-4 h-4" /> Hallazgos ({data.stats.total})
                </button>
                <button onClick={() => setTab('trends')} data-testid="tab-trends"
                    className="px-4 py-2 text-sm font-bold border-b-2 inline-flex items-center gap-2"
                    style={{ borderColor: tab === 'trends' ? ACCENT : 'transparent', color: tab === 'trends' ? ACCENT : '#6B7280' }}>
                    <TrendingUp className="w-4 h-4" /> Tendencias / Patrones
                </button>
            </div>

            {tab === 'trends' ? (
                <TrendsPanel trends={trends} loading={loading} />
            ) : (
            <>
            {/* Filters */}
            <div className="mb-6 flex flex-wrap items-center gap-2">
                <input
                    value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar por cliente, pregunta, norma, análisis..."
                    data-testid="findings-search"
                    className="flex-1 min-w-[240px] px-4 py-3 border outline-none text-sm bg-transparent"
                    style={{ borderColor: 'var(--border)' }}
                />
                <div className="flex gap-1 border p-1" style={{ borderColor: 'var(--border)' }}>
                    {[
                        { v: 'all', label: 'Todos' },
                        { v: 'critical', label: 'Solo críticos' },
                        { v: 'warning', label: 'Solo revisar' },
                    ].map((o) => (
                        <button key={o.v} onClick={() => setFilter(o.v)}
                            data-testid={`filter-${o.v}`}
                            className="px-3 py-2 text-xs font-bold"
                            style={{
                                background: filter === o.v ? ACCENT : 'transparent',
                                color: filter === o.v ? 'white' : 'inherit',
                            }}>
                            {o.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Findings grid */}
            {loading ? (
                <div className="p-12 text-center opacity-60"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : filtered.length === 0 ? (
                <div className="p-16 text-center" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
                    <Shield className="w-10 h-10 mx-auto opacity-40 mb-2" />
                    <div className="opacity-70">
                        {data.stats.total === 0
                            ? 'Aún no hay hallazgos. Cuando los auditores suban fotos, la IA las analizará y aparecerán aquí.'
                            : 'Sin coincidencias con los filtros aplicados.'}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="findings-grid">
                    {filtered.map((it) => (
                        <FindingCard key={it.id || `${it.inspection_id}-${it.item_id}-${it.created_at}`}
                            item={it}
                            deleting={deletingId === it.id}
                            onDelete={() => deleteFinding(it)}
                            onClick={() => setPreview(it)} />
                    ))}
                </div>
            )}
            </>
            )}

            {preview && (
                <PreviewModal item={preview} onClose={() => setPreview(null)} />
            )}
        </AdminLayout>
    );
}

function TrendsPanel({ trends, loading }) {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [recomputing, setRecomputing] = useState(false);

    useEffect(() => {
        api.get('/admin/threshold-config').then((r) => setCfg(r.data)).catch(() => {});
    }, []);

    const saveCfg = async () => {
        setSaving(true);
        try {
            const r = await api.put('/admin/threshold-config', cfg);
            setCfg(r.data);
            toast.success('Configuración de alertas guardada');
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al guardar')); }
        finally { setSaving(false); }
    };
    const recompute = async () => {
        setRecomputing(true);
        try {
            const r = await api.post('/admin/threshold-config/recompute');
            toast.success(`${r.data.alerts_triggered} alerta(s) generada(s) sobre ${r.data.clients_evaluated} cliente(s)`);
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al recomputar')); }
        finally { setRecomputing(false); }
    };

    if (loading) return <div className="p-12 text-center opacity-60"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
    const noData = !trends || trends.totals?.total === 0;
    return (
        <div className="space-y-6" data-testid="trends-panel">
            {/* Configuración de alertas automáticas — visible siempre (incluso sin datos) */}
            {cfg && (
                <div className="p-5 border-l-4" style={{ background: 'rgba(124,107,239,0.06)', borderColor: ACCENT }}
                    data-testid="threshold-config-panel">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-5 h-5" style={{ color: ACCENT }} />
                        <div>
                            <div className="font-display font-black text-lg">Alertas automáticas por umbral</div>
                            <div className="text-xs opacity-70">
                                El admin recibe notificación in-app cuando un cliente supera el umbral configurado.
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 mb-1">Umbral críticos</div>
                            <input type="number" min={1} max={100} value={cfg.critical_threshold}
                                onChange={(e) => setCfg({ ...cfg, critical_threshold: parseInt(e.target.value || 1, 10) })}
                                data-testid="cfg-threshold"
                                className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                                style={{ borderColor: 'var(--border)' }} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 mb-1">Ventana (días)</div>
                            <input type="number" min={1} max={365} value={cfg.window_days}
                                onChange={(e) => setCfg({ ...cfg, window_days: parseInt(e.target.value || 30, 10) })}
                                data-testid="cfg-window"
                                className="w-full px-3 py-2 border outline-none text-sm bg-transparent"
                                style={{ borderColor: 'var(--border)' }} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 mb-1">Activas</div>
                            <label className="inline-flex items-center gap-2 px-3 py-2 border cursor-pointer w-full"
                                style={{ borderColor: 'var(--border)' }}>
                                <input type="checkbox" checked={cfg.enabled}
                                    onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                                    data-testid="cfg-enabled"
                                    className="w-4 h-4" />
                                <span className="text-sm">{cfg.enabled ? 'Sí' : 'No'}</span>
                            </label>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={saveCfg} disabled={saving}
                                data-testid="cfg-save"
                                className="flex-1 text-white px-4 py-2 font-bold inline-flex items-center justify-center gap-1 disabled:opacity-60"
                                style={{ background: ACCENT }}>
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                            </button>
                            <button onClick={recompute} disabled={recomputing}
                                data-testid="cfg-recompute"
                                title="Ejecutar la revisión ahora contra todos los clientes"
                                className="border-2 px-3 py-2 font-bold hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                                style={{ borderColor: 'var(--border)' }}>
                                {recomputing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <div className="text-[11px] opacity-60 mt-3">
                        Regla actual: notificar cuando un cliente acumule <strong>≥ {cfg.critical_threshold}</strong> hallazgos críticos en <strong>{cfg.window_days}</strong> días.
                    </div>
                </div>
            )}

            {/* Bar chart por cliente */}
            {noData ? (
                <div className="p-16 text-center" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
                    <TrendingUp className="w-10 h-10 mx-auto opacity-40 mb-2" />
                    <div className="opacity-70">Aún no hay suficientes datos para calcular tendencias.</div>
                </div>
            ) : (
                <>
            <ChartCard title="Hallazgos por cliente" subtitle="Top 20 clientes ordenados por total de hallazgos">
                <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={trends.by_client} layout="vertical" margin={{ left: 40, right: 20, top: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(107,114,128,0.15)" />
                        <XAxis type="number" stroke="#6B7280" fontSize={11} />
                        <YAxis type="category" dataKey="client_name" stroke="#6B7280" fontSize={11} width={140} />
                        <RTooltip contentStyle={{ background: 'var(--card-bg, #FFF)', border: '1px solid var(--border, #E5E7EB)' }} />
                        <Bar dataKey="critical" stackId="a" fill="#EF4444" name="Crítico" />
                        <Bar dataKey="warning" stackId="a" fill="#F59E0B" name="Revisar" />
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>

            {/* Heatmap por norma */}
            <ChartCard title="Hallazgos por norma" subtitle="Detección de patrones de incumplimiento normativo">
                <div className="space-y-1.5" data-testid="heatmap-standards">
                    {(trends.by_standard || []).map((row) => {
                        const max = Math.max(...trends.by_standard.map((x) => x.total)) || 1;
                        const pct = (row.total / max) * 100;
                        return (
                            <div key={row.standard_reference} className="flex items-center gap-3">
                                <div className="w-48 text-xs font-mono truncate shrink-0">{row.standard_reference}</div>
                                <div className="flex-1 h-6 relative overflow-hidden" style={{ background: 'rgba(107,114,128,0.08)' }}>
                                    <div className="h-full flex">
                                        {row.critical > 0 && (
                                            <div title={`${row.critical} críticos`}
                                                style={{ width: `${(row.critical / max) * 100}%`, background: '#EF4444' }} />
                                        )}
                                        {row.warning > 0 && (
                                            <div title={`${row.warning} para revisar`}
                                                style={{ width: `${(row.warning / max) * 100}%`, background: '#F59E0B' }} />
                                        )}
                                    </div>
                                    <div className="absolute inset-0 flex items-center px-2 text-[11px] font-bold">
                                        <span className="text-white mix-blend-difference">{row.total}</span>
                                    </div>
                                </div>
                                <div className="text-xs opacity-60 w-16 text-right">
                                    {pct.toFixed(0)}%
                                </div>
                            </div>
                        );
                    })}
                </div>
            </ChartCard>

            {/* Line chart por mes */}
            {(trends.by_month || []).length > 1 && (
                <ChartCard title="Evolución mensual" subtitle="Volumen de hallazgos IA por mes">
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={trends.by_month}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(107,114,128,0.15)" />
                            <XAxis dataKey="month" stroke="#6B7280" fontSize={11} />
                            <YAxis stroke="#6B7280" fontSize={11} />
                            <RTooltip contentStyle={{ background: 'var(--card-bg, #FFF)', border: '1px solid var(--border, #E5E7EB)' }} />
                            <Line type="monotone" dataKey="critical" stroke="#EF4444" strokeWidth={2} name="Crítico" dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="warning" stroke="#F59E0B" strokeWidth={2} name="Revisar" dot={{ r: 4 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>
            )}

            {/* Cliente con más críticos — insight card */}
            {trends.by_client?.[0]?.critical > 0 && (
                <div className="p-5 border-l-4" style={{ background: 'rgba(239,68,68,0.08)', borderColor: '#EF4444' }}>
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: '#EF4444' }} />
                        <div className="text-sm">
                            <div className="font-bold mb-1">Cliente con más incidencias críticas</div>
                            <div>
                                <strong>{trends.by_client[0].client_name}</strong> tiene{' '}
                                <strong style={{ color: '#EF4444' }}>{trends.by_client[0].critical}</strong> hallazgo(s) crítico(s) y{' '}
                                <strong style={{ color: '#F59E0B' }}>{trends.by_client[0].warning}</strong> para revisión.
                                Revisa este cliente prioritariamente.
                            </div>
                        </div>
                    </div>
                </div>
            )}
                </>
            )}
        </div>
    );
}

function ChartCard({ title, subtitle, children }) {
    return (
        <div className="p-5 border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
            <div className="mb-3">
                <div className="font-display font-black text-lg">{title}</div>
                {subtitle && <div className="text-xs opacity-60">{subtitle}</div>}
            </div>
            {children}
        </div>
    );
}

function StatCard({ label, value, color, bg, Icon }) {
    return (
        <div className="p-5 border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">{label}</div>
                <div className="w-9 h-9 flex items-center justify-center" style={{ background: bg, color }}>
                    <Icon className="w-5 h-5" />
                </div>
            </div>
            <div className="text-4xl font-display font-black" style={{ color }}>{value}</div>
        </div>
    );
}

function LowConfidenceBadge({ confidence, compact = false }) {
    // Iter 62 · shown when the vision model flagged low confidence (< 0.6).
    // Purely visual — does not block or alter the flow.
    if (confidence === null || confidence === undefined) return null;
    if (confidence >= 0.6) return null;
    const pct = Math.round(confidence * 100);
    const label = compact ? `IA ${pct}%` : `Revisión humana recomendada — baja confianza IA (${pct}%)`;
    return (
        <span
            data-testid={`low-confidence-badge`}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1"
            style={{
                background: 'rgba(234,88,12,0.15)',      // orange-600 @ 15%
                color: '#EA580C',
                border: '1px solid rgba(234,88,12,0.45)',
            }}
            title={`El modelo declaró confianza ${pct}% (< 60%). Verifica la observación antes de decidir.`}
        >
            <AlertTriangle className="w-3 h-3" /> {label}
        </span>
    );
}

function FindingCard({ item, onClick, onDelete, deleting }) {
    const meta = FLAG_META[item.ai_flag] || FLAG_META.warning;
    const lowConf = item.ai_confidence !== null && item.ai_confidence !== undefined && item.ai_confidence < 0.6;
    return (
        <div
            role="button" tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
            data-testid={`finding-card-${item.item_id}`}
            className="relative text-left border transition-all hover:shadow-lg overflow-hidden flex flex-col cursor-pointer"
            style={{ background: 'var(--card-bg)', borderColor: lowConf ? '#EA580C' : meta.color }}>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                disabled={deleting}
                data-testid={`finding-delete-${item.item_id}`}
                title="Eliminar hallazgo"
                className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full shadow-md transition-colors disabled:opacity-60"
                style={{ background: 'rgba(255,255,255,0.92)', color: '#DC2626' }}
            >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
            {item.photo_url ? (
                <div className="relative w-full h-40 bg-black/5">
                    <AuthImage src={item.photo_url} alt="evidencia" className="w-full h-full object-cover" data-testid={`finding-photo-${item.item_id}`} />
                    <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest font-bold px-2 py-1 inline-flex items-center gap-1"
                        style={{ background: meta.bg, color: meta.color }}>
                        {item.ai_flag === 'critical' && <AlertTriangle className="w-3 h-3" />}
                        {meta.label}
                    </span>
                    {lowConf && (
                        <span className="absolute bottom-2 left-2">
                            <LowConfidenceBadge confidence={item.ai_confidence} compact />
                        </span>
                    )}
                </div>
            ) : (
                <div className="w-full h-40 flex items-center justify-center opacity-30">
                    <Camera className="w-8 h-8" />
                </div>
            )}
            <div className="p-4 flex-1 flex flex-col">
                <div className="text-xs opacity-60 mb-1 truncate">
                    {item._client?.company || item._client?.name || 'Cliente'} · {item._inspection?.industry || '—'}
                </div>
                <div className="text-sm font-medium mb-2 line-clamp-2">{item.question}</div>
                <div className="text-xs opacity-70 line-clamp-3 mb-3 flex-1">{item.ai_analysis}</div>
                <div className="flex items-center justify-between text-[11px] opacity-60 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
                    <span>{timeAgo(item.created_at)}</span>
                </div>
            </div>
        </div>
    );
}

function PreviewModal({ item, onClose }) {
    const meta = FLAG_META[item.ai_flag] || FLAG_META.warning;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80" onClick={onClose} />
            <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" style={{ background: 'var(--card-bg)' }}>
                <div className="p-5 border-b flex items-start justify-between gap-3 sticky top-0 z-10"
                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    <div>
                        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 inline-flex items-center gap-1"
                            style={{ background: meta.bg, color: meta.color }}>
                            {item.ai_flag === 'critical' && <AlertTriangle className="w-3 h-3" />}
                            {meta.label}
                        </span>
                        {/* Iter 62 · low-confidence banner inside preview */}
                        <div className="mt-2">
                            <LowConfidenceBadge confidence={item.ai_confidence} />
                        </div>
                        <div className="font-display font-black text-xl mt-2">{item.question}</div>
                        <div className="text-xs opacity-70 mt-0.5">
                            {item._client?.company || item._client?.name || 'Cliente'} · {item._inspection?.industry || '—'}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/5">
                        <ChevronRight className="w-5 h-5 rotate-45" />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    {item.photo_url && (
                        <AuthImage src={item.photo_url} alt="evidencia"
                            className="w-full max-h-[400px] object-contain border"
                            style={{ borderColor: 'var(--border)' }}
                            data-testid={`finding-photo-full-${item.item_id}`} />
                    )}
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-1">Análisis IA</div>
                        <p className="text-sm">{item.ai_analysis}</p>
                    </div>
                    {item.ai_legal_framework && (
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-1">Marco jurídico</div>
                            <p className="text-sm font-mono">{item.ai_legal_framework}</p>
                        </div>
                    )}
                    {item.observations && (
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-1">Observaciones del auditor</div>
                            <p className="text-sm">{item.observations}</p>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="border p-3" style={{ borderColor: 'var(--border)' }}>
                            <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Respuesta original</div>
                            <div className="font-bold">{item.answer || '—'}</div>
                        </div>
                    </div>
                    <Link
                        to={`/admin/inspections/${item.inspection_id}/form`}
                        onClick={onClose}
                        data-testid="preview-goto-inspection"
                        className="inline-flex items-center gap-2 px-4 py-3 text-white font-bold w-full justify-center"
                        style={{ background: ACCENT }}
                    >
                        <ExternalLink className="w-4 h-4" /> Ir a la auditoría
                    </Link>
                </div>
            </div>
        </div>
    );
}
